import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useLang } from '../contexts/LanguageContext';

const gold = '#999999';
const goldLight = '#888888';
const goldAccent = '#555555';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// Validation email simple
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export default function Contact() {
  const { t } = useLang();
  const [formRef, formVis] = useReveal(0.1);
  const [infoRef, infoVis] = useReveal(0.1);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  // Erreurs par champ
  const [errors, setErrors] = useState({});
  // Champs touchés (pour n'afficher l'erreur qu'après interaction)
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState(null); // null | 'loading' | 'success'
  const [successAnim, setSuccessAnim] = useState(0); // 0 -> 1 -> 2 -> 3 (phases d'anim)

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name);
  };

  const validateField = (name) => {
    let error = null;
    const val = formData[name];

    if (name === 'name' && !val.trim()) error = t('contact.errorName');
    if (name === 'email') {
      if (!val.trim()) error = t('contact.errorEmailMissing');
      else if (!isValidEmail(val)) error = t('contact.errorEmailInvalid');
    }
    if (name === 'message' && !val.trim()) error = t('contact.errorMessage');

    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const validateAll = () => {
    const required = ['name', 'email', 'message'];
    const allTouched = {};
    required.forEach(f => allTouched[f] = true);
    setTouched(prev => ({ ...prev, ...allTouched }));

    let valid = true;
    required.forEach(f => {
      if (!validateField(f)) valid = false;
    });
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) return;

    setStatus('loading');
    try {
      await api.post('/messages', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        requestType: 'renseignement',
      });
      setStatus('success');
      setSuccessAnim(1);
      setTimeout(() => setSuccessAnim(2), 600);
      setTimeout(() => setSuccessAnim(3), 1200);
    } catch (err) {
      console.error('Erreur envoi message :', err);
      setStatus(null);
      setErrors(prev => ({ ...prev, message: t('contact.errorGeneric') }));
      setTouched(prev => ({ ...prev, message: true }));
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', message: '' });
    setErrors({});
    setTouched({});
    setStatus(null);
    setSuccessAnim(0);
  };

  const inputClass = `w-full bg-transparent border-b text-sm text-gray-900 py-3 pl-1 focus:outline-none transition-colors placeholder:text-gray-300`;

  // Rendu du champ avec gestion d'erreur
  const renderField = (name, label, type = 'text', placeholder = '', required = false, extraProps = {}) => {
    const hasError = touched[name] && errors[name];
    return (
      <div>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-3">
          {label} {required && '*'}
        </label>
        <input type={type} name={name} value={formData[name]} onChange={handleChange}
          onBlur={() => handleBlur(name)}
          placeholder={placeholder}
          className={inputClass}
          style={{ borderColor: hasError ? '#c47070' : `${gold}30` }}
          onFocus={e => e.target.style.borderColor = hasError ? '#c47070' : gold}
          {...extraProps} />
        <div className={`overflow-hidden transition-all duration-300 ${hasError ? 'max-h-8 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
          <p className="text-xs text-[#c47070]">{errors[name]}</p>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* ── HERO CONTACT ── */}
      <section className="relative aspect-video md:aspect-auto md:h-[80vh] flex items-center justify-center overflow-hidden bg-black">
        <img src="/photos/IMG_2710.jpeg" alt="Contact Pernety"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.85) contrast(1) saturate(0.95)' }} />
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.15)' }} />

        <div className="relative z-10 text-center px-6">
          <div className="flex items-center justify-center gap-3 sm:gap-6 mb-4 sm:mb-10">
            <div className="w-8 sm:w-16 h-px bg-white/30" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-6 sm:h-12 w-auto"
              style={{ filter: 'invert(1) brightness(2)', opacity: 0.5 }} />
            <div className="w-8 sm:w-16 h-px bg-white/30" />
          </div>
          <h1 className="text-white font-light tracking-wider" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(16px, 3.5vw, 52px)'
          }}>
            {t('contact.heroTitle')}
          </h1>
        </div>
      </section>

      {/* ── FORMULAIRE + INFOS ── */}
      <section className="py-24 md:py-32 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">

          {/* ── COLONNE GAUCHE : INFOS ── */}
          <div ref={infoRef}
            className={`lg:col-span-4 transition-all duration-[1.2s] ${infoVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-12" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t('contact.infoLead')}
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border" style={{ borderColor: `${gold}30` }}>
                  <svg className="w-4 h-4" style={{ color: goldAccent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-400 mb-1">{t('contact.emailLabel')}</p>
                  <p className="text-sm text-gray-900">contact@pernetyparis.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border" style={{ borderColor: `${gold}30` }}>
                  <svg className="w-4 h-4" style={{ color: goldAccent }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-400 mb-1">{t('contact.instagramLabel')}</p>
                  <a href="https://www.instagram.com/pernety_jewelry/" target="_blank" rel="noopener noreferrer"
                    className="text-sm text-gray-900 hover:underline">@pernety_jewelry</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border" style={{ borderColor: `${gold}30` }}>
                  <svg className="w-4 h-4" style={{ color: goldAccent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </div>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900">{t('contact.appointmentLabel')}</p>
                </div>
              </div>
            </div>

          </div>

          {/* ── COLONNE DROITE : FORMULAIRE OU CONFIRMATION ── */}
          <div ref={formRef}
            className={`lg:col-span-8 transition-all duration-[1.2s] delay-200 ${formVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

            {/* ── ECRAN DE SUCCES ── */}
            {status === 'success' ? (
              <div className="flex items-center justify-center min-h-[500px]">
                <div className="text-center">
                  {/* Cercle animé avec checkmark */}
                  <div className="relative w-24 h-24 mx-auto mb-10">
                    {/* Cercle qui se dessine */}
                    <svg className="w-24 h-24" viewBox="0 0 96 96">
                      <circle cx="48" cy="48" r="44" fill="none" strokeWidth="1.5"
                        style={{
                          stroke: gold,
                          strokeDasharray: 276,
                          strokeDashoffset: successAnim >= 1 ? 0 : 276,
                          transition: 'stroke-dashoffset 0.8s cubic-bezier(0.22, 1, 0.36, 1)'
                        }} />
                    </svg>
                    {/* Checkmark */}
                    <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10" viewBox="0 0 40 40">
                      <path d="M10 20 L18 28 L30 12" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        style={{
                          stroke: gold,
                          strokeDasharray: 40,
                          strokeDashoffset: successAnim >= 2 ? 0 : 40,
                          transition: 'stroke-dashoffset 0.5s cubic-bezier(0.22, 1, 0.36, 1)'
                        }} />
                    </svg>
                  </div>

                  {/* Texte qui apparait */}
                  <div style={{
                    opacity: successAnim >= 3 ? 1 : 0,
                    transform: successAnim >= 3 ? 'translateY(0)' : 'translateY(15px)',
                    transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)'
                  }}>
                    <div className="flex items-center justify-center gap-4 mb-6">
                      <div className="w-8 h-px" style={{ background: `linear-gradient(90deg, transparent, ${gold})` }} />
                      <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: gold }} />
                      <div className="w-8 h-px" style={{ background: `linear-gradient(90deg, ${gold}, transparent)` }} />
                    </div>

                    <h3 className="font-serif text-2xl md:text-3xl text-gray-900 mb-4">
                      {t('contact.successTitle')}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-3 max-w-md mx-auto">
                      {t('contact.successThanks')(formData.name || t('contact.successDearClient'))}
                    </p>
                    <p className="text-gray-400 text-sm leading-relaxed mb-10 max-w-md mx-auto">
                      {t('contact.successReply')}
                    </p>

                    <button onClick={resetForm}
                      className="px-10 py-4 text-[11px] tracking-[0.25em] uppercase border transition-all duration-500"
                      style={{ borderColor: `${gold}40`, color: '#666' }}
                      onMouseEnter={e => { e.target.style.backgroundColor = '#1a1a1a'; e.target.style.color = '#fff'; e.target.style.borderColor = '#1a1a1a'; }}
                      onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#666'; e.target.style.borderColor = `${gold}40`; }}>
                      {t('contact.sendAnother')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ── FORMULAIRE ── */
              <form onSubmit={handleSubmit} className="space-y-10" noValidate>
                {/* Nom + Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {renderField('name', t('contact.fullName'), 'text', t('contact.fullNamePlaceholder'), true)}
                  {renderField('email', t('contact.emailLabel'), 'email', t('contact.emailPlaceholder'), true)}
                </div>

                {/* Téléphone */}
                {renderField('phone', t('contact.phone'), 'tel', t('contact.phonePlaceholder'))}

                {/* Message */}
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-3">
                    {t('contact.yourMessage')} *
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleChange}
                    onBlur={() => handleBlur('message')}
                    rows="5"
                    className="w-full bg-transparent border-b text-sm text-gray-900 py-3 pl-1 focus:outline-none transition-colors placeholder:text-gray-300 resize-none"
                    style={{ borderColor: touched.message && errors.message ? '#c47070' : `${gold}30` }}
                    onFocus={e => e.target.style.borderColor = touched.message && errors.message ? '#c47070' : gold} />
                  <div className={`overflow-hidden transition-all duration-300 ${touched.message && errors.message ? 'max-h-8 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                    <p className="text-xs text-[#c47070]">{errors.message}</p>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex items-center justify-between pt-4">
                  <p className="text-xs text-gray-300">{t('contact.requiredHint')}</p>
                  <button type="submit" disabled={status === 'loading'}
                    className="relative px-12 py-4 text-white text-[11px] tracking-[0.3em] uppercase transition-all duration-500 disabled:cursor-not-allowed overflow-hidden"
                    style={{ backgroundColor: '#1a1a1a' }}
                    onMouseEnter={e => { if (status !== 'loading') e.target.style.backgroundColor = goldAccent; }}
                    onMouseLeave={e => e.target.style.backgroundColor = '#1a1a1a'}>
                    {status === 'loading' ? (
                      <span className="flex items-center gap-3">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('contact.submitting')}
                      </span>
                    ) : t('contact.submit')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
